import axios, { AxiosInstance } from 'axios';

const API_BASE_URL = (import.meta.env?.VITE_API_URL as string) || 'https://api.sportsground.ru';
const API_URL = `${API_BASE_URL}/api`;

// Callback для обновления состояния при 401 ошибке (избегаем циклических зависимостей)
let onUnauthorizedCallback: (() => void) | null = null;

export const setUnauthorizedCallback = (callback: () => void) => {
  onUnauthorizedCallback = callback;
};

// Создаем экземпляр axios
const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 секунд таймаут
});

// Интерцептор для добавления токена авторизации
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Интерцептор для обработки ошибок
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Токен невалиден или истек
      // Очищаем localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      // Вызываем callback для обновления Redux состояния (если установлен)
      if (onUnauthorizedCallback) {
        onUnauthorizedCallback();
      }
      // Не делаем редирект здесь, чтобы избежать бесконечных циклов
      // Редирект будет обработан в компонентах через проверку isAuthenticated
    }
    return Promise.reject(error);
  }
);

// Утилиты для работы с токеном
export const setToken = (token: string) => {
  localStorage.setItem('authToken', token);
};

export const getToken = (): string | null => {
  return localStorage.getItem('authToken');
};

export const removeToken = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('userId');
};

export const setUserId = (userId: string | number) => {
  localStorage.setItem('userId', userId.toString());
};

export const getUserId = (): string | null => {
  return localStorage.getItem('userId');
};

// API методы
export const getCenters = async () => {
  try {
    const response = await api.get('/sportcomplexes');
    return response.data;
  } catch (error: any) {
    // Если 401, пробуем без токена (публичные данные)
    if (error.response?.status === 401) {
      const publicResponse = await axios.get(`${API_URL}/sportcomplexes`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return publicResponse.data;
    }
    throw error;
  }
};

// Загружаем площадки с прогрессом
// Загрузка ограниченного количества площадок для главной страницы
export const getPlaygroundsLimited = async (limit: number = 6) => {
  const startTime = Date.now();
  console.log(`🚀 Начало загрузки ${limit} площадок для главной...`);
  
  try {
    const publicResponse = await axios.get(`${API_URL}/playgrounds`, {
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });
    const data = publicResponse.data;
    // Ограничиваем количество до limit
    const limitedData = Array.isArray(data) ? data.slice(0, limit) : [];
    const loadTime = ((Date.now() - startTime) / 1000).toFixed(2);
    console.log(`✅ Загружено ${limitedData.length} площадок (ограничено до ${limit}) за ${loadTime}с`);
    return limitedData;
  } catch (publicError: any) {
    console.error('❌ Ошибка загрузки площадок:', publicError);
    return [];
  }
};

export const getPlaygrounds = async (onProgress?: (loaded: number, total: number) => void) => {
  const startTime = Date.now();
  console.log('🚀 Начало загрузки площадок...');
  
  // Проверяем наличие токена
  const token = getToken();
  const requestConfig = {
    timeout: 60000, // Увеличиваем таймаут для большого объема данных
    onDownloadProgress: (progressEvent: any) => {
      if (progressEvent.total && onProgress) {
        const loaded = Math.round((progressEvent.loaded / progressEvent.total) * 100);
        onProgress(loaded, 100);
      }
    },
  };
  
  // Если токена нет, сразу загружаем без токена (публичные данные)
  if (!token) {
    console.log('📋 Токена нет, загружаем публичные данные...');
    try {
      const publicResponse = await axios.get(`${API_URL}/playgrounds`, {
        headers: {
          'Content-Type': 'application/json',
        },
        ...requestConfig,
      });
      const data = publicResponse.data;
      const loadTime = ((Date.now() - startTime) / 1000).toFixed(2);
      console.log(`✅ Загружено ${data.length} площадок (без токена) за ${loadTime}с`);
      return data;
    } catch (publicError: any) {
      console.error('❌ Ошибка загрузки площадок без токена:', publicError);
      return [];
    }
  }
  
  // Если токен есть, пробуем загрузить с токеном
  try {
    const response = await api.get('/playgrounds', requestConfig);
    
    const data = response.data;
    const loadTime = ((Date.now() - startTime) / 1000).toFixed(2);
    console.log(`✅ Загружено ${data.length} площадок за ${loadTime}с`);
    
    return data;
  } catch (error: any) {
    const loadTime = ((Date.now() - startTime) / 1000).toFixed(2);
    console.error(`❌ Ошибка загрузки площадок за ${loadTime}с:`, error);
    
    // Если 401 или CORS ошибка, пробуем без токена (публичные данные)
    if (error.response?.status === 401 || error.code === 'ERR_NETWORK' || error.message?.includes('CORS')) {
      console.log('🔄 Пробуем загрузить без токена...');
      try {
        const publicResponse = await axios.get(`${API_URL}/playgrounds`, {
          headers: {
            'Content-Type': 'application/json',
          },
          ...requestConfig,
        });
        const data = publicResponse.data;
        const loadTime2 = ((Date.now() - startTime) / 1000).toFixed(2);
        console.log(`✅ Загружено ${data.length} площадок (без токена) за ${loadTime2}с`);
        return data;
      } catch (publicError: any) {
        console.error('❌ Ошибка загрузки площадок без токена:', publicError);
        return [];
      }
    }
    
    // Для других ошибок тоже возвращаем пустой массив
    console.log('⚠️ Возвращаем пустой массив из-за ошибки загрузки площадок');
    return [];
  }
};

export const getPlayground = async (id: number | string) => {
  try {
    const response = await api.get(`/playgrounds/${id}`);
    let playground = response.data;
    
    if (!playground) {
      return null;
    }
    
    // Всегда загружаем центры и маппим центр к playground (как в Redux thunk)
    try {
      const centers = await getCenters();
      if (playground.complexId) {
        const center = centers.find((c: any) => c.id === playground.complexId);
        if (center) {
          playground.center = center;
          // Если у playground нет city, но есть у center, используем city из center
          if (!playground.city && center.city) {
            playground.city = center.city;
          }
        }
      }
    } catch (centerError) {
      console.log('⚠️ Не удалось загрузить центры для playground:', centerError);
    }
    
    return playground;
  } catch (error: any) {
    console.error(`❌ Ошибка загрузки playground ${id}:`, error);
    if (error.response?.status === 401 || error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const getPlaygroundMeetings = async (playgroundId: number | string) => {
  try {
    const response = await api.get(`/meetings/playground/${playgroundId}`);
    return response.data || [];
  } catch (error: any) {
    console.error('❌ Ошибка загрузки встреч площадки:', error);
    if (error.response?.status === 401 || error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

// Функция для нормализации встречи из бэкенда (вынесена наружу для использования в catch)
const normalizeMeetingData = (meeting: any): any => {
  if (!meeting || typeof meeting !== 'object') {
    return null;
  }
  
  // Если это уже нормализованная встреча, возвращаем как есть
  if (meeting.date && meeting.time) {
    return meeting;
  }
  
  // Извлекаем данные - может быть вложенная структура Meeting (из userMeetings)
  // или прямой объект (из creatorMeetings или /meetings endpoint)
  let meetingData = meeting;
  
  // Если есть вложенная структура Meeting, извлекаем её
  if (meeting.Meeting && typeof meeting.Meeting === 'object') {
    meetingData = meeting.Meeting;
  }
  
  // Маппинг полей из бэкенда (Meeting.FromDto возвращает: Id, DateTimeStart, PlaygroundId, SportType, CreatorId, PlaceCount, Creator, Users)
  // В доменной модели поле называется Sport, в ViewModel - SportType
  const sportValue = meetingData.SportType || meetingData.Sport || meetingData.sportType || meetingData.sport;
  const sportId = typeof sportValue === 'number' ? sportValue : (sportValue ? parseInt(sportValue, 10) : undefined);
  
  const normalized: any = {
    id: meetingData.Id || meetingData.id,
    playgroundId: meetingData.PlaygroundId || meetingData.playgroundId,
    sportId: sportId,
    creatorId: meetingData.CreatorId || meetingData.creatorId || meetingData.Creator?.Id || meetingData.Creator?.id,
    maxParticipants: meetingData.PlaceCount || meetingData.placeCount,
    // Для подсчета участников проверяем все возможные варианты
    currentParticipants: (() => {
      // Проверяем Users (с большой буквы) - из Meeting.FromDto
      if (meetingData.Users && Array.isArray(meetingData.Users)) {
        return meetingData.Users.length;
      }
      // Проверяем users (с маленькой буквы) - может быть в некоторых ответах
      if (meetingData.users && Array.isArray(meetingData.users)) {
        return meetingData.users.length;
      }
      // Проверяем userIds
      if (meetingData.userIds && Array.isArray(meetingData.userIds)) {
        return meetingData.userIds.length;
      }
      // Проверяем UserCount
      if (meetingData.UserCount !== undefined) {
        return meetingData.UserCount;
      }
      // Проверяем userCount
      if (meetingData.userCount !== undefined) {
        return meetingData.userCount;
      }
      return 0;
    })(),
    // Для массива участников проверяем все возможные варианты
    participants: (() => {
      // Проверяем Users (с большой буквы) - из Meeting.FromDto
      if (meetingData.Users && Array.isArray(meetingData.Users)) {
        return meetingData.Users;
      }
      // Проверяем users (с маленькой буквы) - может быть в некоторых ответах
      if (meetingData.users && Array.isArray(meetingData.users)) {
        return meetingData.users;
      }
      // Проверяем participants
      if (meetingData.participants && Array.isArray(meetingData.participants)) {
        return meetingData.participants;
      }
      // Проверяем UserMeetings - если пришли связи, извлекаем пользователей
      if (meetingData.UserMeetings && Array.isArray(meetingData.UserMeetings)) {
        return meetingData.UserMeetings
          .filter((um: any) => um.User && um.IsActive !== false && um.IsCanceled !== true)
          .map((um: any) => um.User);
      }
      // Проверяем userMeetings (с маленькой буквы)
      if (meetingData.userMeetings && Array.isArray(meetingData.userMeetings)) {
        return meetingData.userMeetings
          .filter((um: any) => um.User && um.IsActive !== false && um.IsCanceled !== true)
          .map((um: any) => um.User);
      }
      return [];
    })(),
  };
  
  // Логирование для отладки
  if (!normalized.sportId) {
    console.log('⚠️ sportId не найден в данных:', {
      SportType: meetingData.SportType,
      Sport: meetingData.Sport,
      sportType: meetingData.sportType,
      sport: meetingData.sport,
      allKeys: Object.keys(meetingData),
    });
  }
  
  // Проверяем, что есть хотя бы id
  if (!normalized.id) {
    console.log('⚠️ Встреча без ID, пропускаем:', meetingData);
    return null;
  }
  
  // Обработка даты и времени (DateTimeStart из бэкенда - это DateTimeOffset)
  if (meetingData.DateTimeStart) {
    // DateTimeStart может быть строкой ISO, объектом DateTimeOffset или DateTime
    let dateTime: Date;
    if (typeof meetingData.DateTimeStart === 'string') {
      dateTime = new Date(meetingData.DateTimeStart);
    } else if (meetingData.DateTimeStart instanceof Date) {
      dateTime = meetingData.DateTimeStart;
    } else {
      // Если это объект DateTimeOffset, может быть поле DateTime или нужно парсить строку
      const dateStr = meetingData.DateTimeStart.DateTime || meetingData.DateTimeStart.dateTime || meetingData.DateTimeStart;
      dateTime = new Date(dateStr);
    }
    
    if (!isNaN(dateTime.getTime())) {
      normalized.date = dateTime.toISOString().split('T')[0];
      normalized.time = dateTime.toTimeString().split(' ')[0].substring(0, 5);
    } else {
      console.log('⚠️ Не удалось распарсить DateTimeStart:', meetingData.DateTimeStart);
    }
  } else if (meetingData.dateTimeStart) {
    const dateTime = new Date(meetingData.dateTimeStart);
    if (!isNaN(dateTime.getTime())) {
      normalized.date = dateTime.toISOString().split('T')[0];
      normalized.time = dateTime.toTimeString().split(' ')[0].substring(0, 5);
    }
  } else if (meetingData.date) {
    normalized.date = meetingData.date;
    normalized.time = meetingData.time || '';
  }
  
  // Если нет даты и времени, пропускаем
  if (!normalized.date || !normalized.time) {
    console.log('⚠️ Встреча без даты/времени, пропускаем. DateTimeStart:', meetingData.DateTimeStart, 'dateTimeStart:', meetingData.dateTimeStart);
    return null;
  }
  
  // Обработка playground (если есть)
  if (meetingData.Playground || meetingData.playground) {
    normalized.playground = meetingData.Playground || meetingData.playground;
  }
  
  // Обработка creator (если есть)
  if (meetingData.Creator || meetingData.creator) {
    normalized.creator = meetingData.Creator || meetingData.creator;
  }
  
  return normalized;
};

export const getMeetings = async (sport?: number) => {
  console.log('🚀 Начало загрузки встреч...');
  console.log('  API_URL:', API_URL);
  console.log('  Token:', getToken() ? 'Есть' : 'Нет');
  console.log('  Sport filter:', sport || 'Все');
  
  const token = getToken();
  if (!token) {
    console.log('⚠️ Нет токена авторизации, возвращаем пустой массив');
    return [];
  }
  
  try {
    // Используем endpoint для получения встреч по виду спорта (как в мобильном приложении)
    // Контроллер: MeetingsController с Route("api/[controller]") = api/Meetings
    // Метод: HttpGet("sport/{sport}") = api/Meetings/sport/{sport}
    // Если sport не указан, пробуем получить все встречи через /Meetings/user
    
    if (sport) {
      // Используем endpoint для встреч по виду спорта
      const url = `/Meetings/sport/${sport}`;
      console.log('  Запрос к:', `${API_URL}${url}`);
      
      const response = await api.get(url);
      const data = response.data;
      
      console.log('✅ Ответ получен:', {
        status: response.status,
        dataType: Array.isArray(data) ? 'array' : typeof data,
        dataLength: Array.isArray(data) ? data.length : 'N/A',
        isArray: Array.isArray(data),
        keys: data && typeof data === 'object' && !Array.isArray(data) ? Object.keys(data) : 'N/A',
        fullData: data,
      });
      
      if (!data) {
        console.log('⚠️ Данные пусты');
        return [];
      }
      
      // Если это массив, нормализуем каждую встречу
      if (Array.isArray(data)) {
        console.log(`📦 Получен массив из ${data.length} элементов`);
        if (data.length === 0) {
          console.warn('⚠️ Массив пуст - встреч нет');
          return [];
        }
        
        console.log('📋 Первая встреча (сырые данные):', JSON.stringify(data[0], null, 2));
        const normalized = data.map((meeting, index) => {
          const result = normalizeMeetingData(meeting);
          if (!result && index === 0) {
            console.error('❌ Первая встреча не прошла нормализацию:', meeting);
          }
          return result;
        }).filter(Boolean);
        
        console.log(`✅ Нормализовано ${normalized.length} встреч из массива (было ${data.length})`);
        if (normalized.length === 0 && data.length > 0) {
          console.error('❌ Все встречи были отфильтрованы при нормализации!');
          console.error('Первая встреча:', JSON.stringify(data[0], null, 2));
        }
        
        // Загружаем данные playground для встреч, у которых есть playgroundId, но нет данных playground
        const meetingsWithPlaygroundIds = normalized.filter(m => m.playgroundId && !m.playground);
        if (meetingsWithPlaygroundIds.length > 0) {
          console.log(`📦 Загружаем данные playground для ${meetingsWithPlaygroundIds.length} встреч...`);
          
          // Загружаем playground параллельно
          const playgroundPromises = meetingsWithPlaygroundIds.map(async (meeting) => {
            try {
              const playground = await getPlayground(meeting.playgroundId);
              return { meetingId: meeting.id, playground };
            } catch (error: any) {
              console.log(`⚠️ Не удалось загрузить playground ${meeting.playgroundId} для встречи ${meeting.id}:`, error.response?.status);
              return { meetingId: meeting.id, playground: null };
            }
          });
          
          const playgroundResults = await Promise.all(playgroundPromises);
          
          // Объединяем данные playground с встречами
          playgroundResults.forEach(({ meetingId, playground }) => {
            if (playground) {
              const meeting = normalized.find(m => m.id === meetingId);
              if (meeting) {
                meeting.playground = playground;
              }
            }
          });
          
          console.log(`✅ Загружены данные playground для встреч`);
        }
        
        return normalized;
      }
      
      // Если это объект, пытаемся извлечь встречи
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        console.log('📦 Получен объект, пытаемся извлечь встречи...');
        const allMeetings: any[] = [];
        
        if (Array.isArray(data.meetings)) {
          allMeetings.push(...data.meetings.map(normalizeMeetingData).filter(Boolean));
        }
        
        Object.values(data).forEach((value: any) => {
          if (Array.isArray(value)) {
            allMeetings.push(...value.map(normalizeMeetingData).filter(Boolean));
          } else if (value && typeof value === 'object') {
            if (value.Meeting) {
              const normalized = normalizeMeetingData(value.Meeting);
              if (normalized) allMeetings.push(normalized);
            } else if (value.id || value.Id || value.DateTimeStart) {
              const normalized = normalizeMeetingData(value);
              if (normalized) allMeetings.push(normalized);
            }
          }
        });
        
        console.log(`✅ Извлечено и нормализовано ${allMeetings.length} встреч из объекта`);
        if (allMeetings.length === 0) {
          console.error('❌ Все встречи были отфильтрованы! Данные:', JSON.stringify(data, null, 2).substring(0, 1000));
        }
        return allMeetings.length > 0 ? allMeetings : [];
      }
      
      console.log('⚠️ Неизвестный формат данных, возвращаем пустой массив');
      return [];
    }
    
    // Если sport не указан, получаем встречи по всем видам спорта и объединяем их
    console.log('  Получаем встречи по всем видам спорта...');
    
    // Получаем список всех кодов видов спорта из справочника
    const { sportCodeToName } = await import('./sports');
    const sportCodes = Object.keys(sportCodeToName).map(code => parseInt(code, 10));
    
    // Запрашиваем встречи по каждому виду спорта параллельно
    const promises = sportCodes.map(async (sportCode) => {
      try {
        const url = `/Meetings/sport/${sportCode}`;
        const response = await api.get(url);
        const data = response.data;
        return Array.isArray(data) ? data : [];
      } catch (error: any) {
        console.log(`  ⚠️ Ошибка загрузки встреч для вида спорта ${sportCode}:`, error.response?.status);
        return [];
      }
    });
    
    const allMeetingsArrays = await Promise.all(promises);
    const data = allMeetingsArrays.flat();
    
    console.log('✅ Объединены встречи по всем видам спорта:', {
      totalMeetings: data.length,
      bySport: sportCodes.map((code, i) => ({ code, count: allMeetingsArrays[i].length })),
    });
    
    if (!data || data.length === 0) {
      console.log('⚠️ Данные пусты');
      return [];
    }
    
    // Нормализуем все встречи
    const normalized = data.map((meeting) => normalizeMeetingData(meeting)).filter(Boolean);
    
    // Загружаем данные playground для встреч, у которых есть playgroundId, но нет данных playground
    const meetingsWithPlaygroundIds = normalized.filter(m => m.playgroundId && !m.playground);
    if (meetingsWithPlaygroundIds.length > 0) {
      console.log(`📦 Загружаем данные playground для ${meetingsWithPlaygroundIds.length} встреч...`);
      
      // Загружаем playground параллельно
      const playgroundPromises = meetingsWithPlaygroundIds.map(async (meeting) => {
        try {
          const playground = await getPlayground(meeting.playgroundId);
          return { meetingId: meeting.id, playground };
        } catch (error: any) {
          console.log(`⚠️ Не удалось загрузить playground ${meeting.playgroundId} для встречи ${meeting.id}:`, error.response?.status);
          return { meetingId: meeting.id, playground: null };
        }
      });
      
      const playgroundResults = await Promise.all(playgroundPromises);
      
      // Объединяем данные playground с встречами
      playgroundResults.forEach(({ meetingId, playground }) => {
        if (playground) {
          const meeting = normalized.find(m => m.id === meetingId);
          if (meeting) {
            meeting.playground = playground;
          }
        }
      });
      
      console.log(`✅ Загружены данные playground для встреч`);
    }
    
    console.log(`✅ Нормализовано ${normalized.length} встреч из всех видов спорта (было ${data.length})`);
    return normalized;
    
    // Если это объект, пытаемся извлечь встречи
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      console.log('📦 Получен объект, пытаемся извлечь встречи...');
      const allMeetings: any[] = [];
      
      if (Array.isArray((data as any).meetings)) {
        allMeetings.push(...(data as any).meetings.map(normalizeMeetingData).filter(Boolean));
      }
      
      Object.values(data).forEach((value: any) => {
        if (Array.isArray(value)) {
          allMeetings.push(...value.map(normalizeMeetingData).filter(Boolean));
        } else if (value && typeof value === 'object') {
          if (value.Meeting) {
            const normalized = normalizeMeetingData(value.Meeting);
            if (normalized) allMeetings.push(normalized);
          } else if (value.id || value.Id || value.DateTimeStart) {
            const normalized = normalizeMeetingData(value);
            if (normalized) allMeetings.push(normalized);
          }
        }
      });
      
      console.log(`✅ Извлечено и нормализовано ${allMeetings.length} встреч из объекта`);
      if (allMeetings.length === 0) {
        console.error('❌ Все встречи были отфильтрованы! Данные:', JSON.stringify(data, null, 2).substring(0, 1000));
      }
      return allMeetings.length > 0 ? allMeetings : [];
    }
    
    console.log('⚠️ Неизвестный формат данных, возвращаем пустой массив');
    return [];
  } catch (error: any) {
    console.error('❌ Ошибка загрузки встреч:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      data: error.response?.data,
    });
    
    // Если 401, пользователь не авторизован
    if (error.response?.status === 401) {
      console.log('⚠️ Требуется авторизация для просмотра встреч');
      // Возвращаем пустой массив вместо ошибки
      return [];
    }
    
    // Если 404, пробуем альтернативные варианты
    if (error.response?.status === 404) {
      console.log('🔄 Endpoint /meetings не найден (404), пробуем альтернативные варианты...');
      
      // Вариант 1: /Meetings/meetings (с большой буквы)
      try {
        let altUrl1 = '/Meetings/meetings';
        if (sport) {
          altUrl1 += `?sport=${sport}`;
        }
        console.log('  Пробуем вариант 1:', `${API_URL}${altUrl1}`);
        const altResponse1 = await api.get(altUrl1);
        const altData1 = altResponse1.data;
        
        console.log('✅ Вариант 1 работает:', {
          status: altResponse1.status,
          dataType: Array.isArray(altData1) ? 'array' : typeof altData1,
          dataLength: Array.isArray(altData1) ? altData1.length : 'N/A',
        });
        
        if (Array.isArray(altData1)) {
          const normalized = altData1.map(normalizeMeetingData).filter(Boolean);
          console.log(`✅ Нормализовано ${normalized.length} встреч через /Meetings/meetings`);
          return normalized;
        }
        
        return [];
      } catch (altError1: any) {
        console.log('  Вариант 1 не сработал, пробуем вариант 2...');
        
        // Вариант 2: /Meetings/user (встречи пользователя, как в мобильном приложении)
        try {
          const altUrl2 = '/Meetings/user';
          console.log('  Пробуем вариант 2:', `${API_URL}${altUrl2}`);
          const altResponse2 = await api.get(altUrl2);
          const altData2 = altResponse2.data;
          
          console.log('✅ Вариант 2 работает:', {
            status: altResponse2.status,
            dataType: Array.isArray(altData2) ? 'array' : typeof altData2,
            dataLength: Array.isArray(altData2) ? altData2.length : 'N/A',
          });
          
          if (Array.isArray(altData2)) {
            const normalized = altData2.map(normalizeMeetingData).filter(Boolean);
            console.log(`✅ Нормализовано ${normalized.length} встреч через /Meetings/user`);
            return normalized;
          }
          
          if (altData2 && typeof altData2 === 'object' && !Array.isArray(altData2)) {
            const allMeetings: any[] = [];
            Object.values(altData2).forEach((value: any) => {
              if (Array.isArray(value)) {
                allMeetings.push(...value.map(normalizeMeetingData).filter(Boolean));
              } else if (value && typeof value === 'object') {
                if (value.Meeting) {
                  const normalized = normalizeMeetingData(value.Meeting);
                  if (normalized) allMeetings.push(normalized);
                } else if (value.id || value.Id || value.DateTimeStart) {
                  const normalized = normalizeMeetingData(value);
                  if (normalized) allMeetings.push(normalized);
                }
              }
            });
            console.log(`✅ Распаковано и нормализовано ${allMeetings.length} встреч через /Meetings/user`);
            return allMeetings.length > 0 ? allMeetings : [];
          }
          
          return [];
        } catch (altError2: any) {
          console.error('❌ Все варианты не сработали:', {
            variant1: altError1.response?.status,
            variant2: altError2.response?.status,
          });
          return [];
        }
      }
    }
    
    // Для других ошибок тоже возвращаем пустой массив, чтобы не ломать UI
    console.log('⚠️ Возвращаем пустой массив из-за ошибки');
    return [];
  }
};

export const getMeeting = async (id: number | string) => {
  try {
    // Пробуем обычный endpoint (как в мобильном приложении)
    // Контроллер: MeetingsController с Route("api/[controller]") = api/Meetings
    // Метод: HttpGet("{id:int}") = api/Meetings/{id}
    const response = await api.get(`/Meetings/${id}`);
    const data = response.data;
    
    console.log('✅ Данные встречи получены:', {
      id: data?.Id || data?.id,
      dateTimeStart: data?.DateTimeStart,
      sport: data?.Sport || data?.SportType || data?.sport || data?.sportType,
      creator: data?.Creator,
      users: data?.Users,
      userCount: data?.Users?.length,
      placeCount: data?.PlaceCount || data?.placeCount,
      allKeys: Object.keys(data || {}),
    });
    
    // Детальное логирование участников
    console.log('👥 Детали участников:', {
      Users: data?.Users,
      users: data?.users, // Проверяем и с маленькой буквы
      UsersType: Array.isArray(data?.Users) ? 'array' : typeof data?.Users,
      usersType: Array.isArray(data?.users) ? 'array' : typeof data?.users,
      UsersLength: Array.isArray(data?.Users) ? data.Users.length : 'N/A',
      usersLength: Array.isArray(data?.users) ? data.users.length : 'N/A',
      UserCount: data?.UserCount,
      userIds: data?.userIds,
      UserMeetings: data?.UserMeetings,
      firstUser: data?.Users?.[0] || data?.users?.[0],
      fullDataUsers: JSON.stringify(data?.Users || data?.users, null, 2),
    });
    
    // Нормализуем данные встречи
    const normalized = normalizeMeetingData(data);
    
    // Дополнительная проверка и исправление sportId если он не был нормализован
    if (!normalized.sportId || normalized.sportId === 0) {
      const sportValue = data?.SportType || data?.Sport || data?.sportType || data?.sport;
      if (sportValue) {
        normalized.sportId = typeof sportValue === 'number' ? sportValue : parseInt(sportValue, 10);
        console.log('🔧 Исправлен sportId:', normalized.sportId);
      }
    }
    
    // Дополнительная проверка и исправление количества участников
    // В бэкенде Meeting.FromDto возвращает Users как массив пользователей
    // Но может быть и users (с маленькой буквы)
    if (data?.Users && Array.isArray(data.Users) && data.Users.length > 0) {
      normalized.participants = data.Users;
      normalized.currentParticipants = data.Users.length;
      console.log('🔧 Исправлено количество участников из Users:', {
        count: normalized.currentParticipants,
        users: data.Users.map((u: any) => ({ id: u.Id || u.id, name: u.Name || u.name })),
      });
    } else if (data?.users && Array.isArray(data.users) && data.users.length > 0) {
      normalized.participants = data.users;
      normalized.currentParticipants = data.users.length;
      console.log('🔧 Исправлено количество участников из users:', {
        count: normalized.currentParticipants,
        users: data.users.map((u: any) => ({ id: u.id, name: u.name })),
      });
    } else if (data?.UserCount !== undefined) {
      normalized.currentParticipants = data.UserCount;
      console.log('🔧 Использовано UserCount из данных:', normalized.currentParticipants);
    } else if (data?.userIds && Array.isArray(data.userIds)) {
      normalized.currentParticipants = data.userIds.length;
      console.log('🔧 Использовано количество из userIds:', normalized.currentParticipants);
    }
    
    // Если участники все еще не найдены, проверяем все возможные варианты
    if (!normalized.participants || normalized.participants.length === 0) {
      console.log('⚠️ Участники не найдены, проверяем все варианты:', {
        Users: data?.Users,
        users: data?.users,
        UserMeetings: data?.UserMeetings,
        userMeetings: data?.userMeetings,
        UserIds: data?.UserIds,
        userIds: data?.userIds,
      });
    }
    
    // Дополнительно обрабатываем поля, которые могут быть в детальной встрече
    if (data) {
      // Обрабатываем comment (описание встречи)
      if (data.Comment !== undefined) {
        normalized.comment = data.Comment;
      } else if (data.comment !== undefined) {
        normalized.comment = data.comment;
      }
      
      // Обрабатываем dateTimeEnd (время окончания)
      if (data.DateTimeEnd) {
        let dateTimeEnd: Date;
        if (typeof data.DateTimeEnd === 'string') {
          dateTimeEnd = new Date(data.DateTimeEnd);
        } else if (data.DateTimeEnd instanceof Date) {
          dateTimeEnd = data.DateTimeEnd;
        } else {
          const dateStr = data.DateTimeEnd.DateTime || data.DateTimeEnd.dateTime || data.DateTimeEnd;
          dateTimeEnd = new Date(dateStr);
        }
        if (!isNaN(dateTimeEnd.getTime())) {
          normalized.timeEnd = dateTimeEnd.toTimeString().split(' ')[0].substring(0, 5);
        }
      } else if (data.dateTimeEnd) {
        const dateTimeEnd = new Date(data.dateTimeEnd);
        if (!isNaN(dateTimeEnd.getTime())) {
          normalized.timeEnd = dateTimeEnd.toTimeString().split(' ')[0].substring(0, 5);
        }
      }
      
      // Обрабатываем isClosed (приватность встречи)
      if (data.IsClosed !== undefined) {
        normalized.isClosed = data.IsClosed;
      } else if (data.isClosed !== undefined) {
        normalized.isClosed = data.isClosed;
      }
      
      // Обрабатываем guests (гости из Telegram)
      if (data.Guests) {
        normalized.guests = data.Guests;
      } else if (data.guests) {
        normalized.guests = data.guests;
      }
    }
    
    console.log('✅ Встреча нормализована:', normalized);
    return normalized;
  } catch (error: any) {
    console.error('❌ Ошибка загрузки встречи:', error);
    // Если не получилось, пробуем с includeExternal (как fallback в мобильном приложении)
    if (error.response?.status === 404 || error.response?.status === 500) {
      try {
        console.log('🔄 Пробуем fallback с includeExternal...');
        const response = await api.get(`/Meetings/${id}?includeExternal=true`);
        const data = response.data;
        return normalizeMeetingData(data);
      } catch (fallbackError: any) {
        console.error('❌ Ошибка загрузки встречи (fallback):', fallbackError);
        throw fallbackError;
      }
    }
    throw error;
  }
};

export const createMeeting = async (data: any) => {
  const response = await api.post('/meetings', data);
  return response.data;
};

// Авторизация по телефону и коду (SMS)
export const auth = async (phone: string, code: string) => {
  const response = await axios.post(
    `${API_BASE_URL}/auth/token`,
    `grant_type=phonecode&username=${encodeURIComponent(phone)}&password=${encodeURIComponent(code)}`,
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    }
  );
  return response.data;
};

// Авторизация по телефону и паролю (для тестов)
export const authWithPassword = async (phone: string, password: string) => {
  // Нормализуем номер телефона: добавляем + если его нет
  let normalizedPhone = phone.trim();
  if (normalizedPhone && !normalizedPhone.startsWith('+')) {
    normalizedPhone = '+' + normalizedPhone;
  }
  
  console.log('[authWithPassword] Исходный номер:', phone, '-> Нормализованный:', normalizedPhone);
  
  try {
    const response = await axios.post(
      `${API_BASE_URL}/auth/token`,
      `grant_type=password&username=${encodeURIComponent(normalizedPhone)}&password=${encodeURIComponent(password)}`,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );
    console.log('[authWithPassword] Успешная авторизация');
    return response.data;
  } catch (error: any) {
    console.error('[authWithPassword] Ошибка авторизации:', error.response?.data || error.message);
    throw error;
  }
};

export const createUser = async (phone: string) => {
  const formData = new FormData();
  formData.append('phone', phone);
  const response = await api.post('/user', formData);
  return response.data;
};

export const getUser = async (id?: number | string) => {
  if (!id) {
    // Если ID не передан, пытаемся получить из localStorage
    const userId = getUserId();
    if (!userId) {
      throw new Error('User ID not found');
    }
    id = userId;
  }
  const response = await api.get(`/user/${id}`);
  return response.data;
};

// Загрузка аватара пользователя
export const getUserAvatar = async (userId: number | string): Promise<string | null> => {
  try {
    const response = await api.get(`/meetings/avatar/${userId}`);
    // API возвращает объект с полем Avatar (base64 строка)
    if (response.data && response.data.Avatar) {
      return `data:image/jpeg;base64,${response.data.Avatar}`;
    }
    return null;
  } catch (error: any) {
    // 404 - это нормально, у пользователя может не быть аватара
    if (error.response?.status === 404) {
      return null;
    }
    console.error('Ошибка загрузки аватара:', error);
    return null;
  }
};

export const getUserByPhone = async (phone: string) => {
  const response = await api.get(`/user/by-phone/${encodeURIComponent(phone)}`);
  return response.data;
};

// Утилита для получения URL изображения
export const getImageUrl = (image: string | { id: number; url?: string } | undefined): string | null => {
  if (!image) return null;
  
  // Если это строка (URL)
  if (typeof image === 'string') {
    // Если это полный URL, возвращаем как есть
    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }
    // Если это относительный путь, добавляем API_URL
    if (image.startsWith('/')) {
      return `${API_URL}${image}`;
    }
    // Если это просто путь без слеша, добавляем /image/
    return `${API_URL}/image/${image}`;
  }
  
  // Если это объект с id
  if (typeof image === 'object' && image.id) {
    return `${API_URL}/image/${image.id}`;
  }
  
  return null;
};

export default api;

